/**
 * MentionList — the `@`-mention palette. Receives the tiptap
 * `SuggestionProps`, renders a cmdk list of workspace members.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Command } from 'cmdk'
import { useSuggestionPosition } from '@/lib/tiptap/suggestionPopup'

export interface MentionItem {
  id: string
  label: string
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export interface MentionListProps {
  items: MentionItem[]
  command: (it: MentionItem) => void
  query?: string
  clientRect?: (() => DOMRect | null) | null
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(function MentionList(
  { items, command, query, clientRect },
  ref,
) {
  const [hover, setHover] = useState(0)
  const pos = useSuggestionPosition(clientRect)

  useEffect(() => {
    setHover(0)
  }, [query, items.length])

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === 'ArrowDown') {
        setHover((h) => (h + 1) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'ArrowUp') {
        setHover((h) => (h - 1 + items.length) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'Enter') {
        const item = items[hover]
        if (item) command(item)
        return true
      }
      return false
    },
  }))

  return (
    <div
      className="z-50 w-64 overflow-hidden rounded-md border border-surface-200 bg-white shadow-lg dark:border-surface-700 dark:bg-surface-800"
      style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : { display: 'none' }}
    >
      <Command shouldFilter={false} label="Mention a workspace member">
        <Command.List>
          {items.length === 0 && <div className="px-3 py-2 text-xs text-surface-500">No matches.</div>}
          {items.map((it, i) => (
            <Command.Item
              key={it.id}
              value={it.label}
              onSelect={() => command(it)}
              onMouseMove={() => setHover(i)}
              className={
                'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs ' +
                (i === hover ? 'bg-surface-100 dark:bg-surface-700' : '')
              }
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-[10px] font-semibold text-white">
                {it.label.slice(0, 2).toUpperCase()}
              </span>
              <span className="truncate">{it.label}</span>
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </div>
  )
})
